import { SelectablePill } from "@/components/form/selectable-pill";
import { useFoodAvoids } from "@/hooks/features/use-food-avoids";
import { IonSkeletonText } from "@ionic/react";
import { memo, useRef, useState } from "react";
import { SearchDislikeFoodModal } from "../../components/search-dislike-food-modal";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface FoodAvoidsPageProps {
  onBack?: () => void;
  isModal?: boolean;
}

const FoodAvoidsPage = memo(function FoodAvoidsPage({ onBack, isModal }: FoodAvoidsPageProps) {
  const pageRef = useRef<HTMLElement>(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);

  const {
    sortedFoodList,
    selectedFoods,
    setSelectedFoods,
    isUpdating,
    isLoading,
    addCustomFood,
    removeCustomFood,
    customFoods,
    handleSubmit,
  } = useFoodAvoids(() => {
    onBack?.();
  });

  const toggleFoodOption = (food: string) => {
    const isCustom = customFoods.includes(food);
    if (isCustom) {
      removeCustomFood(food);
      return;
    }

    setSelectedFoods((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(food)) {
        newSet.delete(food);
      } else {
        newSet.add(food);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="no-scrollbar h-full w-full overflow-y-auto bg-[#fcfbfb]">
        <SettingModalHeader title="Foods You Don't Like" onBack={onBack} isModal={isModal} />
        <div className="px-9 pt-4 pb-20">
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <IonSkeletonText key={i} animated className="h-[42px] w-full rounded-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="no-scrollbar h-full w-full overflow-y-auto bg-[#fcfbfb]">
      <SettingModalHeader
        title="Foods You Don't Like"
        showSaveButton
        onSave={handleSubmit}
        onBack={onBack}
        isModal={isModal}
      />

      <div className="px-9 pt-4 pb-20">
        <div className="my-4 grid grid-cols-2 gap-3">
          {sortedFoodList.map((food) => (
            <SelectablePill
              key={food}
              label={food}
              selected={selectedFoods.has(food)}
              onClick={() => !isUpdating && toggleFoodOption(food)}
            />
          ))}
          <SelectablePill
            plus
            label="Add More"
            onClick={() => !isUpdating && setIsSearchModalOpen(true)}
          />
        </div>
      </div>

      <SearchDislikeFoodModal
        pageRef={pageRef}
        isOpen={isSearchModalOpen}
        onDismiss={() => setIsSearchModalOpen(false)}
        onSave={(foods) => {
          foods.forEach((food) => addCustomFood(food));
        }}
      />
    </div>
  );
});

export default FoodAvoidsPage;
